import { formatDate, dateInputValue } from '../../shared/dateTimeFormatting';
import { useDateTimePreferences } from '../../shared/DateTimeProvider';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  adminApi,
  Tenant,
  type SubscriptionPlan,
  type SubscriptionPlanUpsertRequest,
} from '../../services/adminApi';

const SUBSCRIPTION_STATE_OPTIONS = ['Active', 'Expired', 'Suspended', 'Cancelled'] as const;

type SubscriptionState = typeof SUBSCRIPTION_STATE_OPTIONS[number];
type BillingMode = 'monthly' | 'annual';

type EditDraft = {
  tenantId: string;
  tenantName: string;
  plan: string;
  subscriptionState: SubscriptionState;
  evaluationDays: number;
  subscriptionBillingPeriod: 'Monthly' | 'Annual';
  subscriptionLastPaymentAt: string;
};

type PlanFeatureDraft = {
  featureId?: string;
  text: string;
  icon: string;
  sortOrder: number;
};

type PlanDraft = {
  planId?: string;
  sortOrder: number;
  levelLabel: string;
  planName: string;
  description: string;
  monthlyPrice: string;
  annualPrice: string;
  ctaLabel: string;
  usersDisplay: string;
  supportDisplay: string;
  features: PlanFeatureDraft[];
  isFeatured: boolean;
};

const toPlanDraft = (plan?: SubscriptionPlan): PlanDraft => {
  if (!plan) {
    return {
      sortOrder: 1,
      levelLabel: 'Basic',
      planName: '',
      description: '',
      monthlyPrice: '',
      annualPrice: '',
      ctaLabel: 'Select Plan',
      usersDisplay: '',
      supportDisplay: '',
      features: [{ text: '', icon: '✅', sortOrder: 1 }],
      isFeatured: false,
    };
  }

  const features = Array.isArray(plan.features)
    ? [...plan.features]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((feature, index) => ({
        featureId: feature.featureId,
        text: feature.text,
        icon: feature.icon || '✅',
        sortOrder: feature.sortOrder || index + 1,
      }))
    : [];

  return {
    planId: plan.planId,
    sortOrder: plan.sortOrder || 1,
    levelLabel: plan.levelLabel || '',
    planName: plan.planName || '',
    description: plan.description || '',
    monthlyPrice: plan.monthlyPrice || '',
    annualPrice: plan.annualPrice || '',
    ctaLabel: plan.ctaLabel || 'Select Plan',
    usersDisplay: plan.usersDisplay || '',
    supportDisplay: plan.supportDisplay || '',
    features: features.length > 0 ? features : [{ text: '', icon: '✅', sortOrder: 1 }],
    isFeatured: !!plan.isFeatured,
  };
};

const toUpsertRequest = (draft: PlanDraft): SubscriptionPlanUpsertRequest => ({
  levelLabel: draft.levelLabel.trim(),
  planName: draft.planName.trim(),
  description: draft.description.trim() || undefined,
  monthlyPrice: draft.monthlyPrice.trim() || undefined,
  annualPrice: draft.annualPrice.trim() || undefined,
  ctaLabel: draft.ctaLabel.trim(),
  usersDisplay: draft.usersDisplay.trim() || undefined,
  supportDisplay: draft.supportDisplay.trim() || undefined,
  sortOrder: draft.sortOrder,
  isFeatured: draft.isFeatured,
  features: draft.features
    .map((feature, index) => ({
      featureId: feature.featureId,
      text: feature.text.trim(),
      icon: feature.icon.trim() || undefined,
      sortOrder: feature.sortOrder || index + 1,
    }))
    .filter((feature) => feature.text),
});

const getPlanPrice = (plan: SubscriptionPlan, billingMode: BillingMode): string => {
  const raw = billingMode === 'annual' ? plan.annualPrice : plan.monthlyPrice;
  const value = (raw || '').trim();
  if (!value) return 'Contact';
  if (value.toLowerCase() === 'contact') return 'Contact';
  if (value === '0') return 'Free';
  return `$${value}`;
};

const formatLocalDate = (value: string | null | undefined): string => {
  return formatDate(value, { dateStyle: 'medium' });
};

const isEvaluationPlan = (planName: string | null | undefined): boolean =>
  (planName || '').trim().toLowerCase() === 'evaluation';

export const SubscriptionsManagement: React.FC = () => {
  useDateTimePreferences();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState<string>('All');
  const [stateFilter, setStateFilter] = useState<string>('All');
  const [defaultEvaluationDays, setDefaultEvaluationDays] = useState<number>(30);
  const [evaluationCardEnabled, setEvaluationCardEnabled] = useState<boolean>(false);
  const [loadingEvaluationConfig, setLoadingEvaluationConfig] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editDraft, setEditDraft] = useState<EditDraft | null>(null);
  const [planCatalog, setPlanCatalog] = useState<SubscriptionPlan[]>([]);
  const [loadingPlanCatalog, setLoadingPlanCatalog] = useState(true);
  const [billingMode, setBillingMode] = useState<BillingMode>('monthly');
  const [showPlanBuilder, setShowPlanBuilder] = useState(false);
  const [planDraft, setPlanDraft] = useState<PlanDraft | null>(null);

  const loadTenants = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await adminApi.getTenants({ skip: 0, take: 500 });
      setTenants(Array.isArray(result?.tenants) ? result.tenants : []);
    } catch (err: any) {
      setError(err?.message || 'Failed to load tenants');
      setTenants([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPlanCatalog = useCallback(async () => {
    setLoadingPlanCatalog(true);
    try {
      const data = await adminApi.getSubscriptionPlans();
      setPlanCatalog(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err?.message || 'Failed to load plan catalogue');
      setPlanCatalog([]);
    } finally {
      setLoadingPlanCatalog(false);
    }
  }, []);

  const loadEvaluationConfig = useCallback(async () => {
    setLoadingEvaluationConfig(true);
    try {
      const config = await adminApi.getEvaluationCardConfig();
      setEvaluationCardEnabled(!!config?.enabled);
      setDefaultEvaluationDays(Number.isFinite(config?.defaultDays)
        ? Math.max(1, Math.min(3650, Math.round(config.defaultDays)))
        : 30);
    } catch (err: any) {
      setError(err?.message || 'Failed to load evaluation card configuration');
      setEvaluationCardEnabled(false);
      setDefaultEvaluationDays(30);
    } finally {
      setLoadingEvaluationConfig(false);
    }
  }, []);

  useEffect(() => {
    void loadTenants();
    void loadPlanCatalog();
    void loadEvaluationConfig();
  }, [loadTenants, loadPlanCatalog, loadEvaluationConfig]);

  const planOptions = useMemo(() => {
    const dynamic = planCatalog.map((plan) => (plan.planName || '').trim()).filter(Boolean);
    return Array.from(new Set(dynamic));
  }, [planCatalog]);

  const levelSuggestions = useMemo(() => {
    const values = planCatalog.map((plan) => (plan.levelLabel || '').trim()).filter(Boolean);
    return Array.from(new Set(values));
  }, [planCatalog]);

  const filteredTenants = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return tenants.filter((tenant) => {
      const state = tenant.subscriptionState || 'Active';
      const stateMatches = stateFilter === 'All' || state === stateFilter;
      const planMatches = planFilter === 'All' || (tenant.plan || '') === planFilter;
      if (!stateMatches) return false;
      if (!planMatches) return false;
      if (!needle) return true;
      return (
        (tenant.name || '').toLowerCase().includes(needle)
        || (tenant.slug || '').toLowerCase().includes(needle)
        || (tenant.plan || '').toLowerCase().includes(needle)
      );
    });
  }, [tenants, search, stateFilter, planFilter]);

  const totals = useMemo(() => {
      const countBy = (state: string) => tenants.filter((tenant) => (tenant.subscriptionState || 'Active') === state).length;
    return {
      all: tenants.length,
      active: countBy('Active'),
      expired: countBy('Expired'),
      suspended: countBy('Suspended'),
      cancelled: countBy('Cancelled'),
    };
  }, [tenants]);

  const orderedPlans = useMemo(
    () => [...planCatalog].sort((a, b) => a.sortOrder - b.sortOrder || a.planName.localeCompare(b.planName)),
    [planCatalog],
  );

  const openEditor = (tenant: Tenant) => {
    setMessage(null);
    setError(null);
    setEditDraft({
      tenantId: tenant.tenantId,
      tenantName: tenant.name,
      plan: planOptions.includes(tenant.plan || '') ? (tenant.plan || '') : (planOptions[0] || ''),
      subscriptionState: ((tenant.subscriptionState || 'Active') as SubscriptionState),
      evaluationDays: tenant.planDaysLeft && tenant.planDaysLeft > 0
        ? tenant.planDaysLeft
        : defaultEvaluationDays,
      subscriptionBillingPeriod: ((tenant.subscriptionBillingPeriod || 'Monthly') as 'Monthly' | 'Annual'),
      subscriptionLastPaymentAt: tenant.subscriptionLastPaymentAt
        ? dateInputValue(tenant.subscriptionLastPaymentAt)
        : dateInputValue(new Date()),
    });
    setShowEditor(true);
  };

  const closeEditor = () => {
    if (saving) return;
    setShowEditor(false);
    setEditDraft(null);
  };

  const saveDefaultEvaluationDays = async () => {
    const clamped = Math.max(1, Math.min(3650, Math.round(defaultEvaluationDays || 30)));
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const updated = await adminApi.updateEvaluationCardConfig({
        enabled: evaluationCardEnabled,
        defaultDays: clamped,
      });
      setDefaultEvaluationDays(updated.defaultDays);
      setEvaluationCardEnabled(updated.enabled);
      setMessage(`Default evaluation duration saved: ${updated.defaultDays} days.`);
    } catch (err: any) {
      setError(err?.message || 'Failed to save evaluation policy.');
    } finally {
      setSaving(false);
    }
  };

  const toggleEvaluationCard = async (enabled: boolean) => {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const updated = await adminApi.updateEvaluationCardConfig({
        enabled,
        defaultDays: Math.max(1, Math.min(3650, Math.round(defaultEvaluationDays || 30))),
      });
      setEvaluationCardEnabled(updated.enabled);
      setDefaultEvaluationDays(updated.defaultDays);
      setMessage(updated.enabled
        ? 'Evaluation card enabled for tenant-facing plan selector.'
        : 'Evaluation card disabled for tenant-facing plan selector.');
    } catch (err: any) {
      setError(err?.message || 'Failed to update evaluation card policy.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTenantSubscription = async () => {
    if (!editDraft) return;

    if (!editDraft.plan.trim()) {
      setError('A valid plan from the catalogue is required.');
      return;
    }

    if (!SUBSCRIPTION_STATE_OPTIONS.includes(editDraft.subscriptionState as any)) {
      setError('Invalid subscription state selected.');
      return;
    }

    if (isEvaluationPlan(editDraft.plan)) {
      if (!Number.isFinite(editDraft.evaluationDays) || editDraft.evaluationDays < 1 || editDraft.evaluationDays > 3650) {
        setError('Evaluation days must be between 1 and 3650.');
        return;
      }
    }

    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await adminApi.updateTenant(editDraft.tenantId, {
        plan: editDraft.plan.trim(),
        subscriptionState: editDraft.subscriptionState,
        evaluationDays: isEvaluationPlan(editDraft.plan) ? editDraft.evaluationDays : undefined,
        subscriptionBillingPeriod: editDraft.subscriptionBillingPeriod,
        subscriptionLastPaymentAt: isEvaluationPlan(editDraft.plan) ? undefined : editDraft.subscriptionLastPaymentAt,
      });

      setMessage(`Updated subscription for ${editDraft.tenantName}.`);
      setShowEditor(false);
      setEditDraft(null);
      await loadTenants();
    } catch (err: any) {
      setError(err?.message || 'Failed to update tenant subscription.');
    } finally {
      setSaving(false);
    }
  };

  const openCreatePlan = () => {
    const nextOrder = orderedPlans.length > 0 ? orderedPlans[orderedPlans.length - 1].sortOrder + 1 : 1;
    const empty = toPlanDraft();
    empty.sortOrder = nextOrder;
    setPlanDraft(empty);
    setShowPlanBuilder(true);
  };

  const openEditPlan = (plan: SubscriptionPlan) => {
    setPlanDraft(toPlanDraft(plan));
    setShowPlanBuilder(true);
  };

  const closePlanBuilder = () => {
    setShowPlanBuilder(false);
    setPlanDraft(null);
  };

  const removePlan = async (planId: string) => {
    if (!window.confirm('Delete this plan from the catalog?')) return;
    try {
      await adminApi.deleteSubscriptionPlan(planId);
      await loadPlanCatalog();
      setMessage('Plan removed from catalogue.');
    } catch (err: any) {
      setError(err?.message || 'Failed to delete plan.');
    }
  };

  const savePlanDraft = async () => {
    if (!planDraft) return;
    if (!planDraft.planName.trim()) {
      setError('Plan name is required.');
      return;
    }
    if (!planDraft.levelLabel.trim()) {
      setError('Level label is required.');
      return;
    }
    if (!planDraft.ctaLabel.trim()) {
      setError('Selection button label is required.');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const payload = toUpsertRequest(planDraft);
      if (planDraft.planId) {
        await adminApi.updateSubscriptionPlan(planDraft.planId, payload);
        setMessage(`Plan "${planDraft.planName}" updated.`);
      } else {
        await adminApi.createSubscriptionPlan(payload);
        setMessage(`Plan "${planDraft.planName}" added.`);
      }
      closePlanBuilder();
      await loadPlanCatalog();
    } catch (err: any) {
      setError(err?.message || 'Failed to save plan.');
    } finally {
      setSaving(false);
    }
  };

  const updateFeature = (index: number, changes: Partial<PlanFeatureDraft>) => {
    setPlanDraft((prev) => {
      if (!prev) return prev;
      const next = [...prev.features];
      next[index] = { ...next[index], ...changes };
      return { ...prev, features: next };
    });
  };

  const removeFeature = (index: number) => {
    setPlanDraft((prev) => {
      if (!prev) return prev;
      const next = prev.features.filter((_, idx) => idx !== index);
      return {
        ...prev,
        features: next.length > 0 ? next : [{ text: '', icon: '✅', sortOrder: 1 }],
      };
    });
  };

  const addFeature = () => {
    setPlanDraft((prev) => {
      if (!prev) return prev;
      const nextOrder = prev.features.length > 0
        ? Math.max(...prev.features.map((feature) => feature.sortOrder || 0)) + 1
        : 1;
      return {
        ...prev,
        features: [...prev.features, { text: '', icon: '✅', sortOrder: nextOrder }],
      };
    });
  };

  return (
    <div className="management-section">
      <div className="section-header">
        <h2>Subscriptions</h2>
      </div>

      {error ? <div className="error-message" style={{ marginBottom: '0.75rem' }}>{error}</div> : null}
      {message ? <div className="success-message" style={{ marginBottom: '0.75rem' }}>{message}</div> : null}

      <div className="detail-card" style={{ marginBottom: '1rem' }}>
        <h4>Execution Plan</h4>
        <div className="help-text" style={{ marginBottom: '0.5rem' }}>
          1) Define plan catalog cards and features in SuperAdmin.
        </div>
        <div className="help-text" style={{ marginBottom: '0.5rem' }}>
          2) Use side-by-side preview as selector-ready visual baseline.
        </div>
        <div className="help-text">
          3) Apply plan names directly to tenant subscriptions from this page.
        </div>
      </div>

      <div className="detail-card" style={{ marginBottom: '1rem' }}>
        <h4>Plan Catalogue Builder</h4>
        <div className="plan-catalog-toolbar">
          <div className="plan-billing-toggle" role="group" aria-label="Billing mode toggle">
            <button className={`btn-sm ${billingMode === 'monthly' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setBillingMode('monthly')}>Monthly</button>
            <button className={`btn-sm ${billingMode === 'annual' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setBillingMode('annual')}>Annual</button>
          </div>
          <button className="btn-primary" onClick={openCreatePlan}>+ Add Plan Card</button>
        </div>
        <div className="help-text" style={{ marginBottom: '0.6rem' }}>
          The plan catalogue controls card content. Evaluation visibility and default duration are governed by global evaluation policy below.
        </div>
        <div className="form-group" style={{ marginBottom: '0.8rem' }}>
          <label>
            <input
              type="checkbox"
              checked={evaluationCardEnabled}
              disabled={saving || loadingPlanCatalog || loadingEvaluationConfig}
              onChange={() => {
                void toggleEvaluationCard(!evaluationCardEnabled);
              }}
            />{' '}
            Enable Evaluation card (free tier with evaluation timer)
          </label>
        </div>
        <div className="help-text" style={{ marginBottom: '0.8rem' }}>
          Flat model: each plan has a free-text level label and a left-to-right sort order.
        </div>

        {loadingPlanCatalog ? (
          <div className="loading">Loading plan catalogue...</div>
        ) : (
          <div className="plan-preview-grid">
            {orderedPlans.map((plan) => (
              <div key={plan.planId} className={`plan-preview-card${plan.isFeatured ? ' featured' : ''}`}>
                <div className="plan-preview-header">
                  <span className="plan-level-chip">{plan.levelLabel || 'Level'}</span>
                  <div style={{ display: 'flex', gap: '0.3rem' }}>
                    <button className="btn-sm btn-secondary" onClick={() => openEditPlan(plan)}>Edit</button>
                    <button className="btn-sm btn-danger" onClick={() => void removePlan(plan.planId)}>Delete</button>
                  </div>
                </div>
                <h3>{plan.planName || 'Untitled Plan'}</h3>
                <p className="plan-description">{plan.description || 'No description yet.'}</p>
                <div className="plan-price">{getPlanPrice(plan, billingMode)}</div>
                <div className="plan-price-subtext">{billingMode === 'annual' ? 'per month, billed annually' : 'per month'}</div>
                <button className="btn-primary plan-select-btn">{plan.ctaLabel || 'Select Plan'}</button>
                <div className="plan-meta-line"><strong>Users:</strong> {plan.usersDisplay || '-'}</div>
                <div className="plan-meta-line"><strong>Support:</strong> {plan.supportDisplay || '-'}</div>
                <div className="plan-feature-list">
                  {(plan.features || []).length === 0 ? <div className="help-text">No features added.</div> : null}
                  {(plan.features || [])
                    .slice()
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map((feature) => (
                      <div key={feature.featureId} className="plan-feature-item">
                        <span className="plan-feature-icon" aria-hidden="true">{feature.icon || '✅'}</span>
                        <span>{feature.text}</span>
                      </div>
                    ))}
                </div>
              </div>
            ))}
            {orderedPlans.length === 0 && (
              <div className="empty-state" style={{ gridColumn: '1 / -1' }}>
                <div className="empty-state-icon">🧩</div>
                <h3>No plans configured yet</h3>
                <p>Add your first plan card to start the catalogue.</p>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="detail-card" style={{ marginBottom: '1rem' }}>
        <h4>Evaluation Policy</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Default Evaluation Duration (days)</label>
            <input
              type="number"
              min={1}
              max={3650}
              value={defaultEvaluationDays}
              onChange={(e) => setDefaultEvaluationDays(Number(e.target.value || 30))}
            />
            <span className="help-text">Used as default when a tenant is moved to Evaluation from this page.</span>
            <span className="help-text">Plan selector is tokenised from current plan names in this catalogue.</span>
          </div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button className="btn-secondary" onClick={saveDefaultEvaluationDays}>Save Default</button>
          </div>
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: '1rem' }}>
        <div className="stat-card"><h3>Total Tenants</h3><p className="stat-value">{totals.all}</p></div>
        <div className="stat-card"><h3>Active</h3><p className="stat-value">{totals.active}</p></div>
        <div className="stat-card"><h3>Expired</h3><p className="stat-value">{totals.expired}</p></div>
        <div className="stat-card"><h3>Suspended</h3><p className="stat-value">{totals.suspended}</p></div>
        <div className="stat-card"><h3>Cancelled</h3><p className="stat-value">{totals.cancelled}</p></div>
      </div>

      <div className="filters">
        <input
          type="text"
          className="users-search"
          placeholder="Search tenant name or slug"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}>
          <option value="All">All Plans</option>
          {planOptions.map((plan) => (
            <option key={plan} value={plan}>{plan}</option>
          ))}
        </select>
        <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
          <option value="All">All States</option>
          {SUBSCRIPTION_STATE_OPTIONS.map((state) => (
            <option key={state} value={state}>{state}</option>
          ))}
        </select>
        <button className="btn-secondary" onClick={() => { setSearch(''); setPlanFilter('All'); setStateFilter('All'); }}>Clear</button>
      </div>

      {loading ? (
        <div className="loading">Loading subscription data...</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Tenant</th>
              <th>Plan</th>
              <th>Subscription State</th>
              <th>Plan Days Left</th>
              <th>Plan Expiry</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredTenants.map((tenant) => {
              const state = tenant.subscriptionState || 'Active';
              return (
                <tr key={tenant.tenantId}>
                  <td>
                    <strong>{tenant.name}</strong>
                    <div className="help-text">{tenant.slug}</div>
                  </td>
                  <td><span className={`plan-badge ${String(tenant.plan || '').toLowerCase()}`}>{tenant.plan || '-'}</span></td>
                  <td><span className={`status-badge ${String(state).toLowerCase()}`}>{state}</span></td>
                  <td>{tenant.planDaysLeft != null ? String(tenant.planDaysLeft) : '-'}</td>
                  <td>{formatLocalDate(tenant.planExpiry || null)}</td>
                  <td>
                    <button className="btn-secondary btn-sm" onClick={() => openEditor(tenant)}>Edit</button>
                  </td>
                </tr>
              );
            })}
            {filteredTenants.length === 0 && (
              <tr>
                <td colSpan={6} className="empty-row">No tenants found for current filters.</td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {showEditor && editDraft && (
        <div className="modal-overlay" onClick={closeEditor}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Update Subscription: {editDraft.tenantName}</h3>
            <div className="form-group">
              <label>Plan</label>
              <select
                value={editDraft.plan}
                onChange={(e) => setEditDraft((prev) => prev ? { ...prev, plan: e.target.value } : prev)}
                disabled={saving}
              >
                {planOptions.map((plan) => <option key={plan} value={plan}>{plan}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Subscription State</label>
              <select
                value={editDraft.subscriptionState}
                onChange={(e) => setEditDraft((prev) => prev ? { ...prev, subscriptionState: e.target.value as SubscriptionState } : prev)}
                disabled={saving}
              >
                {SUBSCRIPTION_STATE_OPTIONS.map((state) => <option key={state} value={state}>{state}</option>)}
              </select>
            </div>
            {isEvaluationPlan(editDraft.plan) && (
              <div className="form-group">
                <label>Evaluation Duration (days)</label>
                <input
                  type="number"
                  min={1}
                  max={3650}
                  value={editDraft.evaluationDays}
                  onChange={(e) => setEditDraft((prev) => prev ? { ...prev, evaluationDays: Number(e.target.value || defaultEvaluationDays) } : prev)}
                  disabled={saving}
                />
                <span className="help-text">This resets evaluation expiry from now.</span>
              </div>
            )}
            {!isEvaluationPlan(editDraft.plan) && (
              <>
                <div className="form-group">
                  <label>Billing Period</label>
                  <select
                    value={editDraft.subscriptionBillingPeriod}
                    onChange={(e) => setEditDraft((prev) => prev ? { ...prev, subscriptionBillingPeriod: e.target.value as 'Monthly' | 'Annual' } : prev)}
                    disabled={saving}
                  >
                    <option value="Monthly">Monthly</option>
                    <option value="Annual">Annual</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Last Payment Date</label>
                  <input
                    type="date"
                    value={editDraft.subscriptionLastPaymentAt}
                    onChange={(e) => setEditDraft((prev) => prev ? { ...prev, subscriptionLastPaymentAt: e.target.value } : prev)}
                    disabled={saving}
                  />
                </div>
              </>
            )}
            <div className="form-actions">
              <button className="btn-secondary" onClick={closeEditor} disabled={saving}>Cancel</button>
              <button className="btn-primary" onClick={handleSaveTenantSubscription} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPlanBuilder && planDraft && (
        <div className="modal-overlay" onClick={closePlanBuilder}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <h3>{planDraft.planId ? 'Edit Plan Card' : 'Create Plan Card'}</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Sort Order</label>
                <input
                  type="number"
                  min={1}
                  value={planDraft.sortOrder}
                  onChange={(e) => setPlanDraft((prev) => prev ? { ...prev, sortOrder: Number(e.target.value || 1) } : prev)}
                />
              </div>
              <div className="form-group">
                <label>Level Label (free text)</label>
                <input
                  list="plan-level-suggestions"
                  value={planDraft.levelLabel}
                  onChange={(e) => setPlanDraft((prev) => prev ? { ...prev, levelLabel: e.target.value } : prev)}
                  placeholder="e.g. Start Evaluation"
                />
                <datalist id="plan-level-suggestions">
                  {levelSuggestions.map((level) => <option key={level} value={level} />)}
                </datalist>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Plan Name</label>
                <input
                  value={planDraft.planName}
                  onChange={(e) => setPlanDraft((prev) => prev ? { ...prev, planName: e.target.value } : prev)}
                  placeholder="e.g. Starter"
                />
              </div>
              <div className="form-group">
                <label>Selection Button Label</label>
                <input
                  value={planDraft.ctaLabel}
                  onChange={(e) => setPlanDraft((prev) => prev ? { ...prev, ctaLabel: e.target.value } : prev)}
                  placeholder="e.g. Start Evaluation"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Description</label>
              <input
                value={planDraft.description}
                onChange={(e) => setPlanDraft((prev) => prev ? { ...prev, description: e.target.value } : prev)}
                placeholder="Best for..."
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Monthly Price</label>
                <input
                  value={planDraft.monthlyPrice}
                  onChange={(e) => setPlanDraft((prev) => prev ? { ...prev, monthlyPrice: e.target.value } : prev)}
                  placeholder="79 or Contact"
                />
              </div>
              <div className="form-group">
                <label>Annual Price (effective monthly)</label>
                <input
                  value={planDraft.annualPrice}
                  onChange={(e) => setPlanDraft((prev) => prev ? { ...prev, annualPrice: e.target.value } : prev)}
                  placeholder="69 or Contact"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Number of Users</label>
                <input
                  value={planDraft.usersDisplay}
                  onChange={(e) => setPlanDraft((prev) => prev ? { ...prev, usersDisplay: e.target.value } : prev)}
                  placeholder="e.g. Up to 20 users"
                />
              </div>
              <div className="form-group">
                <label>Support</label>
                <input
                  value={planDraft.supportDisplay}
                  onChange={(e) => setPlanDraft((prev) => prev ? { ...prev, supportDisplay: e.target.value } : prev)}
                  placeholder="e.g. Community Support"
                />
              </div>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={!!planDraft.isFeatured}
                  onChange={(e) => setPlanDraft((prev) => prev ? { ...prev, isFeatured: e.target.checked } : prev)}
                />{' '}
                Highlight this plan
              </label>
            </div>

            <div className="form-group">
              <label>Features (text + icon/image symbol)</label>
              {planDraft.features.map((feature, index) => (
                <div key={`${feature.featureId || 'new'}-${index}`} className="form-row" style={{ marginBottom: '0.5rem' }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <input
                      value={feature.icon}
                      onChange={(e) => updateFeature(index, { icon: e.target.value })}
                      placeholder="Icon (e.g. ✅ or image URL)"
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <input
                      value={feature.text}
                      onChange={(e) => updateFeature(index, { text: e.target.value })}
                      placeholder="Feature text"
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <button
                      className="btn-sm btn-danger"
                      type="button"
                      onClick={() => removeFeature(index)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
              <button className="btn-secondary btn-sm" type="button" onClick={addFeature}>+ Add Feature</button>
            </div>

            <div className="form-actions">
              <button className="btn-secondary" onClick={closePlanBuilder}>Cancel</button>
              <button className="btn-primary" onClick={() => void savePlanDraft()} disabled={saving}>
                {saving ? 'Saving...' : 'Save Plan Card'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
